import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  Ticket,
  CheckCircle,
  XCircle,
  Loader2,
  ShoppingCart,
  CreditCard,
  Gift,
  Tag,
  Shield,
  Star
} from 'lucide-react';

/**
 * UnifiedCheckout - Universal checkout component for all products
 * 
 * Usage:
 * <UnifiedCheckout
 *   isOpen={showCheckout}
 *   onClose={() => setShowCheckout(false)}
 *   product={{
 *     id: "membresia-titular",  // For catalog products
 *     // OR
 *     type: "webinar",
 *     itemId: "webinar-uuid",
 *     name: "Webinar Premium",
 *     price: 1499,
 *     description: "Description here"
 *   }}
 *   onSuccess={(result) => console.log('Paid!', result)}
 * />
 */
const UnifiedCheckout = ({ 
  isOpen, 
  onClose, 
  product, 
  onSuccess,
  successUrl,
  cancelUrl
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [finalPrice, setFinalPrice] = useState(product?.price || 0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (product) {
      setFinalPrice(product.price || 0);
      setDiscountAmount(0);
      setAppliedCoupon(null);
      setCouponCode('');
      
      // Fetch product details if it's a catalog product
      if (product.id && !product.type) {
        fetchProductDetails(product.id);
      } else {
        setProductDetails(product);
      }
    }
  }, [product]);

  const fetchProductDetails = async (productId) => {
    try {
      const response = await api.get(`/checkout/products/${productId}`);
      setProductDetails(response.data);
      setFinalPrice(response.data.price);
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const originalPrice = productDetails?.price || product?.price || 0;
      const response = await api.post('/coupons/validate', {
        code: couponCode.trim(),
        item_type: product?.type || 'membership',
        item_id: product?.itemId || product?.id || '',
        original_price: originalPrice
      });

      if (response.data.valid) {
        setAppliedCoupon({
          code: couponCode.toUpperCase(),
          message: response.data.message,
          coupon_id: response.data.coupon_id
        });
        setDiscountAmount(response.data.discount_amount);
        setFinalPrice(response.data.final_price);
        toast({
          title: "¡Cupón aplicado!",
          description: response.data.message
        });
      } else {
        toast({
          title: "Cupón no válido",
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo validar el cupón",
        variant: "destructive"
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setDiscountAmount(0);
    setFinalPrice(productDetails?.price || product?.price || 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para continuar con la compra",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      const checkoutData = {
        coupon_code: appliedCoupon?.code || null,
        quantity: 1,
        success_url: successUrl,
        cancel_url: cancelUrl
      };

      // Determine if catalog product or dynamic product
      if (product?.id && !product?.type) {
        checkoutData.product_id = product.id;
      } else {
        checkoutData.product_type = product?.type || 'membership';
        checkoutData.item_id = product?.itemId;
      }

      const response = await api.post('/checkout/create-session', checkoutData);
      
      if (response.data.checkout_url) {
        if (response.data.session_id === 'free_access') {
          // Free access granted
          toast({
            title: "¡Acceso otorgado!",
            description: "Tu cupón te dio acceso gratuito"
          });
          onSuccess && onSuccess({ free: true, ...response.data });
          onClose();
        } else {
          // Redirect to Stripe checkout
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo procesar la compra",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!product) return null;

  const displayProduct = productDetails || product;
  const originalPrice = displayProduct?.price || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Confirmar Compra
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Product Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{displayProduct?.name}</h3>
                <p className="text-sm text-gray-600">{displayProduct?.description}</p>
              </div>
              {displayProduct?.type === 'membership' && (
                <Badge className="bg-blue-100 text-blue-700 ml-2">
                  <Star className="h-3 w-3 mr-1" />
                  Membresía
                </Badge>
              )}
            </div>
            
            {/* Features list */}
            {displayProduct?.features && displayProduct.features.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Incluye:</p>
                <ul className="space-y-1">
                  {displayProduct.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-center text-xs text-gray-600">
                      <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>Precio</span>
              <span>${originalPrice?.toLocaleString()} MXN</span>
            </div>
            
            {discountAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span className="flex items-center gap-1">
                  <Tag className="h-4 w-4" />
                  Descuento
                </span>
                <span>-${discountAmount.toLocaleString()} MXN</span>
              </div>
            )}
            
            <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg">
              <span>Total a pagar</span>
              <span className={finalPrice === 0 ? 'text-green-600' : ''}>
                {finalPrice === 0 ? '¡GRATIS!' : `$${finalPrice.toLocaleString()} MXN`}
              </span>
            </div>
          </div>

          {/* Coupon Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              ¿Tienes un cupón de descuento?
            </label>
            
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">{appliedCoupon.code}</p>
                    <p className="text-xs text-green-600">{appliedCoupon.message}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeCoupon}
                  className="text-green-700 hover:text-green-800"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Ingresa tu código"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="flex-1"
                  data-testid="coupon-input"
                />
                <Button
                  variant="outline"
                  onClick={validateCoupon}
                  disabled={validatingCoupon || !couponCode.trim()}
                  data-testid="apply-coupon-btn"
                >
                  {validatingCoupon ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Ticket className="h-4 w-4 mr-1" />
                      Aplicar
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Checkout Button */}
          <Button
            className="w-full h-12 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white text-lg"
            onClick={handleCheckout}
            disabled={processing || !isAuthenticated}
            data-testid="checkout-btn"
          >
            {processing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : finalPrice === 0 ? (
              <>
                <Gift className="h-5 w-5 mr-2" />
                Obtener Acceso Gratuito
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Pagar ${finalPrice.toLocaleString()} MXN
              </>
            )}
          </Button>

          {!isAuthenticated && (
            <p className="text-sm text-red-500 text-center">
              Debes iniciar sesión para continuar
            </p>
          )}

          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <Shield className="h-3 w-3" />
            Pago seguro procesado por Stripe
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedCheckout;
